import styled from "styled-components"
import { IroColorPicker } from "./IroColourPicker"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Button, Icon, InvertTheme, MaterialButton, Panel, PanelState, Swatch, TertiaryCtaButton } from "slate-ui-components"
import { useAppDispatch, useAppSelector } from "./store/hooks"
import { addCustomWrap, optionClick, removeCustomWrap, VisualizerSlice } from "./store/reducers/visualizer"
import { CustomWrapFinish } from "./types/VisualizerTypes"
import { useIntl } from "react-intl"
import { useAnalytics } from "./AnalyticsProvider"

const CloseButton = styled(Button)`
	position: absolute;
	top: 20px;
	right: 20px;
	padding: 10px;
	margin: -10px;
`

const Container = styled.div`
	display: flex;
	flex-flow: column nowrap;
	justify-content: center;
	align-items: center;
	gap: 20px;	
`

const RemoveButton = styled(TertiaryCtaButton)`
	min-width: unset;
`
const ConfirmButton = styled(TertiaryCtaButton)``

const Centered = styled.div`
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-evenly;
	align-items: center;
	width: 100%;
	max-width: 500px;

	&>div:last-of-type {
		display: contents;
		&>div {
			display: contents !important;
		}
		@media (max-width: 389px) {
			display: flex;
		}
	}

`
const CTAs = styled.div`
	display: flex;
	flex-flow: row nowrap;
	gap: 10px;
	max-width: 500px;
	width: 100%;
`

const MaterialOptions = styled.div`
	display: flex;
	flex-flow: column nowrap;
	gap: 10px;
`

const ButtonContent = styled.span`
	overflow: hidden;
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	gap: 12px;
	height: 28px;
	position: relative;
	white-space: nowrap;
	&>svg {
		height: 100%;
	}
`

export const useCustomColourPanel = () => {
	const intl = useIntl()
	const {record} = useAnalytics()
	const dispatch = useAppDispatch()
	const [state, updateState] = useState<PanelState>("closed")
	const [finish, setFinish] = useState<CustomWrapFinish>("gloss")
	const [diffuse, setDiffuse] = useState<{ r: number, g: number, b: number }>({ r: 220, g: 220, b: 220 })
	const [hsv, setHSV] = useState<{h?: number, s?: number, v?: number} | undefined>(undefined)
	const currentWrapId = useAppSelector(s => s.configuration.currentCustomWrap);
	const currentWrap = useAppSelector(s => s.configuration.optionGroups["wrap"].options[s.configuration.currentCustomWrap!] ?? undefined);
	const activeWrap = useAppSelector(s => s.configuration.selectedOptions["wrap"]?.map(id => s.configuration.optionGroups["wrap"].options[id]?.customWrap).find(() => true));
	const quantize = useAppSelector(s => s.configuration.currentMode) !== "3D"

	const specular = useMemo(() => {
		switch(finish) {
			case "gloss":
				return {r: 10, g: 10, b: 10}
			case "satin":
				return {r: 10, g: 10, b: 10}				
			case "matte":
				return {r: 10, g: 10, b: 10}
			/*
			case "metallic": {
				// Standard dielectric specular color in linear space
				const unityColorSpaceDielectricSpec = { r: 0.04, g: 0.04, b: 0.04 };
				const metallic = 0.35

				return {
					r: diffuse.r * metallic + unityColorSpaceDielectricSpec.r * (1 - metallic),
					g: diffuse.g * metallic + unityColorSpaceDielectricSpec.g * (1 - metallic),
					b: diffuse.b * metallic + unityColorSpaceDielectricSpec.b * (1 - metallic),
				};
			}
			*/
		}
	}, [/*diffuse,*/ finish])
	
	const open = useCallback(() =>
	{
		if(activeWrap && !currentWrap?.customWrap) {
			setDiffuse(activeWrap.diffuse);
			setFinish(activeWrap.finish);
		}
		
		updateState(prevState => prevState !== "open" ? "opening" : "open")
		record?.(["Configurator", "ColourPanelOpen"])
	}, [record, activeWrap, currentWrap])
	
	const close = useCallback(() => {
		updateState(prevState => {
			if(prevState !== "closed") {
				if(prevState !== "closing") {
					record?.(["Configurator", "ColourPanelClose"])
				}
				return "closing"
			}
			return "closed"
		})
	}, [record])


	const onColourChange =  useCallback((colour: {rgb: {r: number, g: number, b: number}, hsv: {h?: number, s?: number, v?: number}}) => {
		setDiffuse(colour.rgb)
		setHSV(colour.hsv)
	}, [])

	useEffect(() =>
	{
		if(currentWrap?.customWrap?.diffuse)
			setDiffuse(currentWrap?.customWrap?.diffuse);
		if(currentWrap?.customWrap?.finish)
			setFinish(currentWrap?.customWrap?.finish);
	},[currentWrapId])

	useEffect(() => {		
		if(state === "closed") {
			dispatch(VisualizerSlice.actions.setPreviewWrap(undefined))
		}
		else {
			dispatch(VisualizerSlice.actions.setPreviewWrap({diffuse: diffuse, specular: specular, finish, selected: true, created: 0}))
		}
	}, [dispatch, diffuse, specular, finish, state])

	const element = useMemo(() => {
		const _addCustomWrap = () => {
			const id = currentWrapId ?? crypto.randomUUID();
			dispatch(addCustomWrap(
				id,
				{
					diffuse,
					specular,
					finish,
					created: Date.now(),
					selected: false
				}
			))
			dispatch(VisualizerSlice.actions.deselectAllCustomWraps());
			dispatch(VisualizerSlice.actions.focusOption({optionGroup: "wrap", optionId: id}))
			dispatch(optionClick({ group: "wrap", option: id }))
			dispatch(VisualizerSlice.actions.editWrapDone());
			record?.(["Configurator", "AddCustomWrap"], {diffuse, hsv, finish})
			close()
		}

		const _removeCustomWrap = () => {
			if(currentWrapId) {
				dispatch(removeCustomWrap(currentWrapId))
				record?.(["Configurator", "DeleteCustomWrap"])
			}
			dispatch(VisualizerSlice.actions.editWrapDone())
			close()
		}

		const title = intl.formatMessage({id: "configuration.custom_wrap", defaultMessage: "Custom Wrap"})
		const removeLabel = intl.formatMessage({id: "configuration.remove_custom_wrap", defaultMessage: "Remove Custom Wrap"})
		const addLabel = currentWrapId ?
			intl.formatMessage({id: "configuration.edit_custom_wrap", defaultMessage: "Edit Custom Wrap"})
			:
			intl.formatMessage({id: "configuration.add_custom_wrap", defaultMessage: "Add Custom Wrap"})

		const finishColour = "#383838"
		const customColour = `rgb(${diffuse.r},${diffuse.g},${diffuse.b})`

		return (
			<InvertTheme>
				<Panel state={state} updateState={updateState} title={title}>
					<Container>
						<Centered>
							<MaterialOptions>
								<MaterialButton 
									colour={finishColour}
									finish="gloss"
									label={intl.formatMessage({id: "finish_type.gloss", defaultMessage: "Gloss"})}
									onClick={() => setFinish("gloss")}
									selected={finish === "gloss"}
									testId="finish-gloss"
								/>
								<MaterialButton 
									colour={finishColour}
									finish="satin"
									label={intl.formatMessage({id: "finish_type.satin", defaultMessage: "Satin"})}
									onClick={() => setFinish("satin")}
									selected={finish === "satin"}
									testId="finish-satin"
								/>
								<MaterialButton 
									colour={finishColour}
									finish="matte"
									label={intl.formatMessage({id: "finish_type.matte", defaultMessage: "Matte"})}
									onClick={() => setFinish("matte")}
									selected={finish === "matte"}
									testId="finish-matte"
								/>
							</MaterialOptions>
							<IroColorPicker defaultValue={diffuse} onChange={onColourChange} quantize={quantize}/>
							
						</Centered>
						<CTAs>
							{currentWrapId &&
								<RemoveButton label={removeLabel} onClick={_removeCustomWrap} testId="remove-custom-wrap">
									<Icon icon="Trash"/>
								</RemoveButton>
							}
							<ConfirmButton label={addLabel} onClick={_addCustomWrap} testId="add-custom-wrap" $fill>
								<ButtonContent>
									<Swatch colour={customColour} finish={finish}/>
									{addLabel}
								</ButtonContent>
								<Icon icon="Arrow"/>
							</ConfirmButton>
						</CTAs>
						<CloseButton label={intl.formatMessage({id: "general.close", defaultMessage: "Close"})} onClick={close}>
							<Icon icon="Close"/>
						</CloseButton>
					</Container>
				</Panel>
			</InvertTheme>
		)
	}, [intl, diffuse, state, finish, onColourChange, currentWrapId, dispatch, specular, record, hsv, close, quantize])

	return {
		open,
		close,
		element
	}
}